/* Listings read layer.
 *
 * Column note, verified against the live proxy: the listings table has NO
 * created_at column — `order=created_at.desc` returns PostgREST 42703 and a
 * 400. So recency ordering is not available on this table and is not faked.
 * The rail's order is: server-tagged listings first, then the backend's own
 * order, untouched.
 *
 * HONEST SIGNALS. Every badge a card draws names the column it reads, in a
 * `data-field` on the badge itself: the purpose badge reads `listing_type`,
 * the video badge reads `video_url`. View counts render only when the server
 * reports a count above zero. Nothing in this module invents a signal, and
 * there is no client-side "popular" or "trending" computation.
 * scripts/verify-honest-signals.mjs holds every rendered badge to the row the
 * page was actually sent.
 *
 * `has_video` IS NOT READ, ANYWHERE. It is a second copy of a fact the row
 * already states in `video_url`, and the copy drifted: four listings carried
 * `has_video = true` beside `video_url = NULL`, so the home page drew
 * «فيديو» on four cards with nothing to play (CARD-BADGES-1, 2026-10-01). A
 * listing has a video when its `video_url` is one this site can play, and
 * for no other reason. The column itself is retired later, with L1.
 */

import { apiGet } from "./client";
import { galleryOf, youTubeId } from "@/lib/media";

export type ListingRow = {
  id: string;
  title: string | null;
  type: string | null;
  listing_type: string | null;
  city_text: string | null;
  district_text: string | null;
  country_id: number | null;
  city_id: number | null;
  district_id: number | null;
  currency: string | null;
  price: number | null;
  status: string | null;
  beds: number | null;
  baths: number | null;
  area: number | null;
  furnished: boolean | null;
  description: string | null;
  views: number | null;
  photo_url: string | null;
  tag: string | null;
  kitchens: number | null;
  lat: number | null;
  lng: number | null;
  location_precise: boolean | null;
  location_address: string | null;
  location_address_ar: string | null;
  inquiries: number | null;
  owner_id?: string | null;
  flag_reason?: string | null;
  /* Migration 002 / proxy v3.8.3. Optional because every listing created
   * before them has neither, and an older proxy returns neither. */
  photo_urls?: string[] | null;
  video_url?: string | null;
  owner_plan?: string | null;
  owner_subscription_status?: string | null;
  /* Migration 004. Nullable, and absent on every row created before it. */
  licence_number?: string | null;
  /* Migration 014 / worker v3.8.14. Null = never confirmed, and nothing is drawn. */
  availability_confirmed_at?: string | null;
  refreshed_at?: string | null;
};

export type Listing = {
  id: string;
  title: string;
  /** "Sale" | "Rent" as the backend spells it; mapped to copy at render time. */
  listingType: string | null;
  cityText: string | null;
  districtText: string | null;
  cityId: number | null;
  /** Which market owns it. Needed to keep a market's pages to its own stock. */
  countryId: number | null;
  districtId: number | null;
  currency: string | null;
  price: number | null;
  beds: number | null;
  baths: number | null;
  area: number | null;
  furnished: boolean;
  photoUrl: string | null;
  /** Server-set badge. Never synthesised on the client. */
  tag: string | null;
  /** Server-reported view count. Rendered only when > 0. */
  views: number;
  /** True only when `video_url` is a link the detail page can play. Derived,
   *  never read from a column of its own -- see the header. */
  hasVideo: boolean;
  /** Deterministic placeholder hue for photoless cards (legacy hueForId). */
  imageHue: number;
  /** Owner tier as the SERVER reports it; drives promo placement only. */
  ownerTier: string;
  /** Who to send an inquiry to. Exposed by the public feed. */
  ownerId: string | null;
  type: string | null;
  kitchens: number | null;
  description: string | null;
  lat: number | null;
  lng: number | null;
  /** The lister's privacy choice. False means show an area, never a pin. */
  locationPrecise: boolean;
  locationAddress: string | null;
  locationAddressAr: string | null;
  inquiries: number;
  /** Admin moderation note. Shown to the OWNER on a rejected listing. */
  flagReason: string | null;
  /** The ordered gallery. Falls back to [photoUrl] for older listings, so a
   *  single-photo listing is indistinguishable from a one-item gallery. */
  photoUrls: string[];
  /** Canonical YouTube watch URL, or null. */
  videoUrl: string | null;
  /** The advertising licence number, where the market requires one. */
  licenceNumber: string | null;
  /** Server-owned. The member panel shows it; nothing client-side sets it. */
  status: string | null;
  /** When the lister last said it is still available. Null = never; the card
   *  and the page draw nothing for it, not a dash and not «غير مؤكد». */
  availabilityConfirmedAt: string | null;
  /** The fresh key: when the lister last refreshed it. */
  refreshedAt: string | null;
};

/* Copied from /legacy (EjarMasr.dc.html, hueForId): a stable hue per listing
 * id so a photoless card keeps the same striped placeholder between loads. */
export function hueForId(id: string): number {
  const str = String(id);
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) % 360;
  return h;
}

export function toListing(row: ListingRow): Listing {
  return {
    id: row.id,
    title: row.title ?? "",
    listingType: row.listing_type,
    cityText: row.city_text,
    districtText: row.district_text,
    cityId: row.city_id,
    countryId: row.country_id,
    districtId: row.district_id,
    currency: row.currency,
    price: typeof row.price === "number" ? row.price : null,
    beds: row.beds,
    baths: row.baths,
    area: row.area,
    furnished: !!row.furnished,
    photoUrl: row.photo_url,
    tag: row.tag,
    views: typeof row.views === "number" && row.views > 0 ? row.views : 0,
    hasVideo: youTubeId(row.video_url) !== null,
    imageHue: hueForId(row.id),
    ownerTier: ownerTierOf(row),
    ownerId: row.owner_id ?? null,
    type: row.type,
    kitchens: row.kitchens,
    description: row.description,
    lat: typeof row.lat === "number" ? row.lat : null,
    lng: typeof row.lng === "number" ? row.lng : null,
    locationPrecise: !!row.location_precise,
    locationAddress: row.location_address,
    locationAddressAr: row.location_address_ar,
    inquiries: typeof row.inquiries === "number" ? row.inquiries : 0,
    flagReason: row.flag_reason ?? null,
    photoUrls: galleryOf(row.photo_urls, row.photo_url),
    videoUrl: row.video_url ?? null,
    licenceNumber: row.licence_number ?? null,
    status: row.status,
    availabilityConfirmedAt: row.availability_confirmed_at ?? null,
    refreshedAt: row.refreshed_at ?? null,
  };
}

/**
 * Featured rail source. Server-tagged listings lead; everything else keeps the
 * backend's order. A stable sort means untagged listings are never reshuffled.
 */
/* HOW "FEATURED" IS DECIDED, in full: Featured, then Hot, then New, then
 * everything else, and within a band the backend's own order is kept.
 *
 * It used to ask only whether `tag` was non-null, which ranked all three
 * EQUALLY -- a listing tagged New tied with a paid Featured one. Bassem's
 * ruling in HOME-1: "order by tag priority Featured > Hot > New > untagged".
 * This is that ruling. A tag the server invents tomorrow that is not in the
 * list ranks above untagged and below New, rather than vanishing.
 *
 * The sort is stable (ECMAScript requires it), so nothing inside a band is
 * reshuffled between renders. */
export const TAG_RANK = ["Featured", "Hot", "New"] as const;

function rankOf(tag: string | null): number {
  if (!tag) return TAG_RANK.length + 1;
  const i = (TAG_RANK as readonly string[]).indexOf(tag);
  return i === -1 ? TAG_RANK.length : i;
}

/* The fresh key (L1a): the more recently REFRESHED listing first, and a listing that
 * was never refreshed after one that was -- never "treated as the oldest date", which
 * would be a value invented for it. Used only to break ties inside a rank; ranking
 * weights themselves are L2. */
export function fresher(a: Listing, b: Listing): number {
  if (!a.refreshedAt && !b.refreshedAt) return 0;
  if (!a.refreshedAt) return 1;
  if (!b.refreshedAt) return -1;
  return new Date(b.refreshedAt).getTime() - new Date(a.refreshedAt).getTime();
}

export function orderForRail(listings: Listing[]): Listing[] {
  return [...listings].sort((a, b) => rankOf(a.tag) - rankOf(b.tag) || fresher(a, b));
}

export async function fetchFeaturedListings(
  limit = 12,
  signal?: AbortSignal,
  countryId?: number | null,
): Promise<Listing[]> {
  const rows = await apiGet<ListingRow>(
    `listings?status=eq.Active`
    + (countryId != null ? `&country_id=eq.${encodeURIComponent(String(countryId))}` : "")
    + `&limit=${encodeURIComponent(String(limit))}`,
    signal,
  );
  return orderForRail(rows.map(toListing));
}

/** One lister's ACTIVE listings, for their public profile. */
export async function fetchListingsByOwner(
  ownerId: string, signal?: AbortSignal,
): Promise<Listing[]> {
  if (!ownerId) return [];
  /* The proxy forces status=Active on every public /listings read, so this
   * cannot return a draft, a paused or a rejected listing however it is
   * called. Nothing here has to remember that rule -- it is enforced above. */
  const rows = await apiGet<ListingRow>(
    `listings?owner_id=eq.${encodeURIComponent(ownerId)}&limit=48`, signal);
  return rows.map(toListing);
}

/* ── Browse / results ─────────────────────────────────────────────────────
 * Filters are pushed to PostgREST rather than applied after the fact, so the
 * server does the narrowing and the client never holds the whole table.
 * Unknown filter values are rejected here rather than dropped -- standing
 * ruling (e), and the legacy bug (inventory §6.3) where an unrecognised
 * filter produced an unfiltered whole-table read.
 */

export type ListingType = "Sale" | "Rent";
/* The five words that were hardcoded here. Kept ONLY so older callers and
 * tests still resolve; the list the site offers now comes from
 * property_types, which Admin owns. Nothing should add to this. */
export const PROPERTY_TYPES = ["Apartment", "Villa", "Duplex", "Studio", "Chalet"] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number];

/* A type filter is now checked by SHAPE, not by membership.
 *
 * WHY THE MEMBERSHIP CHECK HAD TO GO. It existed for a real defect
 * (inventory §6.3): an unrecognised filter value used to be DROPPED, which
 * read the whole table and called it a search. The guard against that is
 * that the filter always reaches the query -- not that the value appears in
 * a list compiled into the bundle. Types are data now: Admin can add عيادة
 * this afternoon, and a build from this morning would have refused to search
 * for it while happily showing the tile that offers it.
 *
 * So the rule is the one that actually prevents the defect: a filter value
 * must be a plausible type, and it is always applied. A value that is real
 * but matches nothing returns an empty result, which is the honest answer to
 * "show me clinics" when there are none. */
function assertTypeFilter(ty: string): void {
  if (typeof ty !== "string" || !ty.trim() || ty.length > 40 || /[(),*]/.test(ty)) {
    throw new Error(`Unusable property type filter: ${String(ty)}`);
  }
}

export type ListingFilters = {
  /* WHICH MARKET'S PROPERTY. Every listing read used to be global, which was
   * invisible while one market was open and wrong the moment a second one had
   * a URL: /sa/ resolved to Saudi Arabia, took its currency and its settings,
   * and then listed EGYPTIAN flats under it. A market prefix that does not
   * reach the query is decoration. */
  countryId?: number | null;
  listingType?: ListingType | null;
  cityId?: number | null;
  districtId?: number | null;
  /* SEVERAL TYPES AT ONCE. The v3 design's filter panel is a column of
   * checkboxes -- "apartment AND duplex" is a question a buyer actually asks,
   * and one <select> could not ask it. Sent as PostgREST `type=in.(…)`.
   * `propertyType` remains for the single-value callers. */
  propertyType?: PropertyType | null;
  propertyTypes?: PropertyType[] | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  minBeds?: number | null;
  furnished?: boolean | null;
  /** The design's "video tour only" filter. Asks for rows that carry a
   *  `video_url`, which is the same fact the badge is drawn from. */
  hasVideo?: boolean | null;
  /** Page offset, and the page size. Together these are one server page. */
  offset?: number | null;
  limit?: number | null;
};

/* "area" is the design's fourth sort option («المساحة»). Descending, because
 * "sort by area" on a property portal means the big ones first -- ascending
 * would open on the smallest studio in the city. */
export type SortKey = "newest" | "price_asc" | "price_desc" | "area";

function esc(v: string | number): string {
  return encodeURIComponent(String(v));
}

/** Build the PostgREST query for a filter set. Throws on an unknown value. */
export function buildListingQuery(f: ListingFilters, limit = 200): string {
  // A limit on the filter set wins: it is part of the query the caller asked
  // for, and passing it separately made a paged call silently fetch 200.
  if (f.limit != null && f.limit > 0) limit = f.limit;
  const parts: string[] = ["status=eq.Active"];

  if (f.countryId != null) parts.push(`country_id=eq.${esc(f.countryId)}`);

  if (f.listingType != null) {
    if (f.listingType !== "Sale" && f.listingType !== "Rent") {
      throw new Error(`Unknown listing_type filter: ${String(f.listingType)}`);
    }
    parts.push(`listing_type=eq.${esc(f.listingType)}`);
  }
  if (f.propertyType != null) {
    assertTypeFilter(f.propertyType);
    parts.push(`type=eq.${esc(f.propertyType)}`);
  }
  if (f.propertyTypes && f.propertyTypes.length) {
    // Same rule as the single-value filter, and for the same reason.
    for (const ty of f.propertyTypes) assertTypeFilter(ty);
    parts.push(`type=in.(${f.propertyTypes.map((t) => esc(t)).join(",")})`);
  }
  if (f.cityId != null) parts.push(`city_id=eq.${esc(f.cityId)}`);
  if (f.districtId != null) parts.push(`district_id=eq.${esc(f.districtId)}`);
  if (f.minPrice != null) parts.push(`price=gte.${esc(f.minPrice)}`);
  if (f.maxPrice != null) parts.push(`price=lte.${esc(f.maxPrice)}`);
  if (f.minBeds != null) parts.push(`beds=gte.${esc(f.minBeds)}`);
  if (f.furnished != null) parts.push(`furnished=is.${f.furnished ? "true" : "false"}`);
  // Only the TRUE case filters. "not video-only" is not a filter, it is the
  // absence of one. The question is put to `video_url`, the column the badge
  // and the player read: asked of `has_video`, this returned four listings
  // with no video in them, under a filter that promised nothing else.
  if (f.hasVideo === true) parts.push("video_url=not.is.null");
  if (f.offset != null && f.offset > 0) parts.push(`offset=${esc(f.offset)}`);

  parts.push(`limit=${esc(limit)}`);
  return `listings?${parts.join("&")}`;
}

/* Promo placement, transcribed from /legacy (promoRank + PROMO_RANK).
 *
 * The rank comes from the owner's tier as the SERVER reports it on the row
 * (owner_plan / owner_subscription_status). The proxy does not currently
 * return those columns, so every listing ranks equal and the backend's own
 * order stands -- which is the correct outcome, not a fallback. Nothing here
 * invents a tier, and there is no client-side notion of who deserves
 * placement. If the server starts returning the columns, ranking activates
 * with no code change. */
const PROMO_RANK: Record<string, number> = { platinum: 3, gold: 2, silver: 1, free: 0 };

/* INERT until v3.9.0 exposes owner_plan in the public feed — see
 * docs/V3.9.0-SPEC.md. `/listings` does not return owner_plan today, so this
 * returns "free" for every row. Kept rather than retired because the column
 * is scoped; scripts/verify-promo-rank.mjs fails the day it arrives and this
 * is still flat, so nobody has to remember to check. */
export function ownerTierOf(row: ListingRow): string {
  const plan = row.owner_plan;
  if (typeof plan !== "string" || !plan) return "free";
  const p = plan.toLowerCase();
  return p === "silver" || p === "gold" || p === "platinum" ? p : "free";
}

/** Stable sort: higher promo rank first, original server order preserved within a rank. */
/* INERT until v3.9.0 exposes owner_plan in the public feed — see
 * docs/V3.9.0-SPEC.md. Every listing's tier is "free" today, so this sort is
 * a no-op that preserves the backend's order. Same note as ownerTierOf: it
 * stays, and a verify check catches the day it should start working. */
export function orderByPromoRank(listings: Listing[]): Listing[] {
  return listings
    .map((l, i) => ({ l, i }))
    .sort((a, b) => (PROMO_RANK[b.l.ownerTier] ?? 0) - (PROMO_RANK[a.l.ownerTier] ?? 0) || fresher(a.l, b.l) || a.i - b.i)
    .map((x) => x.l);
}

export function sortListings(listings: Listing[], key: SortKey): Listing[] {
  // "newest" keeps the promo-ranked server order: the table has no created_at
  // column, so there is no recency to sort by and none is invented.
  if (key === "newest") return listings;
  if (key === "area") {
    // Largest first. A listing with no area goes last rather than counting as
    // zero, which would put every incomplete row at one end as if measured.
    return [...listings].sort((a, b) => {
      if (a.area == null) return 1;
      if (b.area == null) return -1;
      return b.area - a.area;
    });
  }
  const dir = key === "price_asc" ? 1 : -1;
  return [...listings].sort((a, b) => {
    if (a.price == null) return 1;
    if (b.price == null) return -1;
    return (a.price - b.price) * dir;
  });
}

export async function fetchListings(
  f: ListingFilters, signal?: AbortSignal,
): Promise<Listing[]> {
  const rows = await apiGet<ListingRow>(buildListingQuery(f), signal);
  return orderByPromoRank(rows.map(toListing));
}

export async function fetchListingById(
  id: string, signal?: AbortSignal,
): Promise<Listing | null> {
  const rows = await apiGet<ListingRow>(`listings?id=eq.${esc(id)}&limit=1`, signal);
  return rows.length ? toListing(rows[0]) : null;
}

/* L1b — the lister's lifecycle (the nine actions, the dashboard's reads) lives
 * in ./lifecycle.ts and is re-exported here so "the listings API" stays one
 * import for the screens. The read layer above stays exactly what it was. */
export * from "./lifecycle";
